import { Box, CircularProgress, styled, Typography } from "@material-ui/core";
import { useState } from "react";
import { describeCine } from "./cine/cine-metadata";
import { CineThumbnailBadge } from "./cine/cine-thumbnail-badge";
import { OfflineCineButton } from "./cine/offline-cine-button";
import { EpocusImageLightbox } from "./epocus-image-lightbox";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_INACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "./epocus-palette";
import type { HydratedExamImage } from "./hooks/use-epocus-exam-images";

// ─── Styled ───────────────────────────────────────────────────────────────────

const ScrollStrip = styled(Box)({
  display: "flex",
  gap: 8,
  overflowX: "auto",
  paddingBottom: 4,
  // Hide scrollbar on WebKit
  "&::-webkit-scrollbar": { display: "none" },
  scrollbarWidth: "none",
});

const ThumbnailButton = styled("button")({
  flex: "0 0 auto",
  width: 80,
  height: 80,
  padding: 0,
  borderRadius: 8,
  overflow: "hidden",
  cursor: "pointer",
  background: "none",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  backgroundColor: EPOCUS_BG,
  "&:focus-visible": {
    outline: `2px solid ${EPOCUS_PRIMARY}`,
    outlineOffset: 2,
  },
  gap: 0,
});

const ThumbImg = styled("img")({
  width: "100%",
  height: 60,
  objectFit: "cover",
  display: "block",
});

const ThumbLabel = styled(Typography)({
  fontSize: rem(10),
  color: EPOCUS_TEXT_SECONDARY,
  lineHeight: 1.2,
  width: "100%",
  textAlign: "center",
  padding: "2px 4px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const PlaceholderBox = styled(Box)({
  width: "100%",
  height: 60,
  backgroundColor: EPOCUS_INACTIVE,
  opacity: 0.15,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// ─── Component ────────────────────────────────────────────────────────────────

export const EpocusImageGallery: FC<{
  images: HydratedExamImage[];
  selectedIndex: number;
  onImageClick: (index: number) => void;
}> = ({ images, selectedIndex, onImageClick }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (images.length === 0) return null;

  const openLightbox = (index: number) => {
    onImageClick(index);
    setLightboxIndex(index);
  };

  return (
    <>
      {images.some((img) => img.cine) && <OfflineCineButton images={images} />}
      <ScrollStrip>
        {images.map((img, index) => (
          <ThumbnailButton
            key={img.id}
            type="button"
            aria-label={[
              img.viewLabel ?? `Image ${index + 1}`,
              img.cine && describeCine(img.cine),
            ]
              .filter(Boolean)
              .join(", ")}
            aria-pressed={index === selectedIndex}
            onClick={() => openLightbox(index)}
            style={{
              border: `2px solid ${index === selectedIndex ? EPOCUS_PRIMARY : EPOCUS_BORDER}`,
            }}
          >
            {img.loading ? (
              <PlaceholderBox>
                <CircularProgress size={18} style={{ color: EPOCUS_PRIMARY }} />
              </PlaceholderBox>
            ) : img.previewUrl ? (
              <ThumbImg
                src={img.previewUrl}
                alt={img.viewLabel ?? `Image ${index + 1}`}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <PlaceholderBox />
            )}
            {img.cine && <CineThumbnailBadge metadata={img.cine} />}
            <ThumbLabel>{img.viewLabel ?? `#${index + 1}`}</ThumbLabel>
          </ThumbnailButton>
        ))}
      </ScrollStrip>

      {lightboxIndex !== null && (
        <EpocusImageLightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
};
